import React from 'react';
import { Sparkles } from 'lucide-react';

// "Small Steps. Real Progress." — realistic, non-boastful child progress stories.
// Real names/details to be filled in once you gather parent permissions.

const stories = [
  {
    name: 'A Child\u2019s Reading Journey',
    ageBand: 'Age 4',
    startingPoint: 'Developing confidence with letter sounds.',
    focus: 'CVC words, oral blending and repeated reading.',
    progress:
      'Began blending and reading simple words independently within a few weeks.',
    parent: null
  },
  {
    name: 'A Child\u2019s Reading Journey',
    ageBand: 'Age 5',
    startingPoint: 'Knew letter names but not letter sounds.',
    focus: 'Pure letter sounds, phonemic awareness and gentle blending.',
    progress:
      'Started reading simple three-letter words with growing confidence.',
    parent: null
  },
  {
    name: 'A Child\u2019s Reading Journey',
    ageBand: 'Age 6',
    startingPoint:
      'Anxious about reading and reluctant to try new words aloud.',
    focus: 'Building confidence, patient reading practice and short sentences.',
    progress:
      'Willingly attempts short sentences and enjoys reading time again.',
    parent: null
  }
];

const SmallStepsProgress = () => {
  return (
    <section
      id="small-steps-progress"
      data-testid="small-steps-progress"
      className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 bg-rose-50 text-rose-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <Sparkles className="h-4 w-4" />
            Small Steps. Real Progress.
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Every Reader Grows at Their Own Pace
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
            These are the kinds of quiet, meaningful shifts we see in children
            who join Read Right Academy — celebrated as they happen, not
            rushed.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 sm:gap-6">
          {stories.map((s, idx) => (
            <article
              key={idx}
              data-testid={`progress-story-${idx}`}
              className="bg-white rounded-2xl border border-slate-100 hover:border-[#3B4BA0]/30 hover:shadow-md transition-all duration-300 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-bold text-[#3B4BA0] uppercase tracking-widest">
                  {s.name}
                </span>
                <span className="text-xs bg-blue-50 text-[#3B4BA0] px-2.5 py-0.5 rounded-full font-semibold">
                  {s.ageBand}
                </span>
              </div>

              <dl className="space-y-4">
                <div>
                  <dt className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Starting Point
                  </dt>
                  <dd className="text-sm text-gray-700 mt-1 leading-relaxed">
                    {s.startingPoint}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Learning Focus
                  </dt>
                  <dd className="text-sm text-gray-700 mt-1 leading-relaxed">
                    {s.focus}
                  </dd>
                </div>
                <div className="bg-emerald-50/50 border-l-2 border-emerald-300 pl-3 py-1 rounded-r">
                  <dt className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">
                    Progress
                  </dt>
                  <dd className="text-sm text-gray-800 mt-1 leading-relaxed">
                    {s.progress}
                  </dd>
                </div>
              </dl>
            </article>
          ))}
        </div>

        <p className="mt-10 text-center text-sm text-gray-500 italic max-w-2xl mx-auto">
          Real parent stories will be added here as families share their
          experiences.
        </p>
      </div>
    </section>
  );
};

export default SmallStepsProgress;
